/**
 * Generic localStorage cache with TTL support
 * Used for caching API responses to reduce server load
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

/**
 * Get cached data if not expired
 * @param key Cache key
 * @param ttl Time-to-live in milliseconds
 * @returns Cached data or null if expired/not found
 */
export function getCachedData<T>(key: string, ttl: number): T | null {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const entry: CacheEntry<T> = JSON.parse(cached);
    const now = Date.now();

    // Check expiry
    if (now - entry.timestamp > ttl) {
      localStorage.removeItem(key);
      return null;
    }

    return entry.data;
  } catch (error) {
    console.warn(`Failed to read cache for key: ${key}`, error);
    return null;
  }
}

/**
 * Set cached data with timestamp
 * @param key Cache key
 * @param data Data to cache
 */
export function setCachedData<T>(key: string, data: T): void {
  try {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (error) {
    console.warn(`Failed to write cache for key: ${key}`, error);
  }
}

/**
 * Clear specific cache entry
 * @param key Cache key
 */
export function clearCache(key: string): void {
  localStorage.removeItem(key);
}

/**
 * Clear all cache entries matching prefix
 * @param prefix Key prefix to match
 */
export function clearCacheByPrefix(prefix: string): void {
  Object.keys(localStorage)
    .filter(key => key.startsWith(prefix))
    .forEach(key => localStorage.removeItem(key));
}
